import { CompanyServiceEnum } from "@/lib/enum/company-service.enum";
import { ContractTypeEnum } from "@/lib/enum/contract-type.enum";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { FormConfig } from "@/lib/formable";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import { PageDefaults } from "@/lib/utility/data/page-data-provider.abstract";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import companyService from "@/services/mrfiktiv/services/companyService";
import contractService from "@/services/mrfiktiv/services/contract-service";
import fleetService from "@/services/mrfiktiv/services/fleetService";
import vehicleService from "@/services/mrfiktiv/services/vehicleService";
import {
  MrfiktivCreateFleetDtoGen,
  MrfiktivFleetViewModelGen,
  MrfiktivReferenceGen,
  MrfiktivUpdateFleetDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { CompanyDataAccessLayer } from "@/store/modules/access-layers/company.access-layer";
import { FleetDataAccessLayer } from "@/store/modules/access-layers/fleet.access-layer";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import VueRouter from "vue-router";
import { Company, ICompany } from "./company.entity";
import { Contract, IContract } from "./contract.entity";
import { PageFilterElement } from "./page-filter-element.entity";
import { IProjectConfiguration, ProjectConfiguration } from "./project-configuration.entity";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { UtmParameter } from "./utm.entity";
import { VehicleReference } from "./vehicle-reference.entity";
import { IVehicle, Vehicle } from "./vehicle.entity";

export type FleetNode = {
  id: string;
  name: string;
  type: "partner" | "company" | "fleet";
  children: FleetNode[];

  obj?: IFleet;

  icon?: string;
  vehicles?: IVehicle[];
  isVehicleLoading?: boolean;
};

export type FleetVehicleNode = FleetNode & {};

export function buildFleetTree(fleets: IFleet[]): FleetNode[] {
  const idToNodeMap: Record<string, FleetNode> = {};

  for (const fleet of fleets) {
    idToNodeMap[fleet.id] = {
      id: fleet.id,
      name: fleet.title,
      type: fleet.companyId ? "company" : "fleet",
      icon: fleet.isRoot ? "mdi-domain" : fleet.companyId ? "mdi-home" : "mdi-map-marker",
      children: [],
      obj: fleet
    };
  }

  const roots: FleetNode[] = [];

  for (const fleet of fleets) {
    const node = idToNodeMap[fleet.id];

    if (!fleet.parentId) {
      roots.push(node);
    } else {
      const parent = idToNodeMap[fleet.parentId];
      if (!parent) {
        continue;
      }
      parent.children.push(node);
    }
  }

  return roots;
}

/**
 * findPathById
 * Sucht in einem (Teil-)Baum nach dem Knoten mit 'targetId'
 * und gibt den Pfad vom aktuellen Root bis dorthin als Array zurück.
 * Falls nicht gefunden, return null.
 */
export function findPathById(root: FleetNode, targetId: string): FleetNode[] | null {
  if (root.id === targetId) {
    return [root];
  }
  for (const child of root.children) {
    const subPath = findPathById(child, targetId);
    if (subPath) {
      // Pfad gefunden -> current root + gefundener subPath
      return [root, ...subPath];
    }
  }
  return null;
}

export function findPathInForest(roots: FleetNode[], targetId: string): FleetNode[] | null {
  for (const root of roots) {
    const path = findPathById(root, targetId);
    if (path) {
      return path; // sobald gefunden -> zurück
    }
  }
  return null; // nichts gefunden
}

@IsFilterable
export class FleetBase implements MrfiktivFleetViewModelGen, ICreateDto<IFleet> {
  /**
   * @inheritdoc
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.fleet.id",
    config: {
      itemCallback: () => FleetDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-fleet"
    }
  })
  id: string;

  /**
   * @inheritdoc
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.fleet.partnerId"
  })
  partnerId: string;

  /**
   * @inheritdoc
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.fleet.title"
  })
  title: string;

  /** Internal description of the fleet object */
  @FormConfig({
    category: "common.nouns.general",
    searchKeywords: ["objects.fleet.description", "common.nouns.general"],
    type: DetailFormComponentsEnum.TEXT_AREA,
    props: {
      label: "objects.fleet.description"
    },
    rules: [],
    clearable: true
  })
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.fleet.description"
  })
  description?: string;

  /**
   * @inheritdoc
   */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  @FilterConfig({
    displayName: "objects.fleet.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  assignees: string[];

  @FilterConfig({
    type: VehicleReference
  })
  refs?: MrfiktivReferenceGen[] | undefined;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId: string;

  @FilterConfig({
    type: ProjectConfiguration
  })
  configuration: IProjectConfiguration;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.company",
    config: {
      itemCallback: () => CompanyDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-company"
    }
  })
  companyId?: string | undefined;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.fleet.isRoot"
  })
  isRoot: boolean;

  // TODO: IMPLEMENT THIS
  rootId?: string | undefined;
  parentId?: string | undefined;
  ancestorIds: string[];

  ancestors: IFleet[] = [];

  utm?: UtmParameter | null;

  /**
   * Fetched company
   */
  _company?: ICompany;

  /**
   * List of vehicles
   */
  _vehicles?: IVehicle[];

  /**
   * List of contracts
   */
  _contracts?: IContract[];

  _companies?: ICompany[];

  _isLoadingCompany = false;
  _isLoadingCompanies = false;
  _isLoadingVehicles = false;
  _isLoadingContracts = false;

  get isLoadingContracts() {
    return this._isLoadingContracts;
  }

  get isLoadingCompany() {
    return this._isLoadingCompany;
  }

  /**
   * Construct object
   */
  constructor(obj?: Partial<FleetBase | MrfiktivFleetViewModelGen>) {
    this.id = obj?.id ?? "";
    this.partnerId = obj?.partnerId ?? "";
    this.userId = obj?.userId ?? "";

    this.isRoot = obj?.isRoot ?? false;
    this.rootId = obj?.rootId;
    this.parentId = obj?.parentId;
    this.ancestorIds = obj?.ancestorIds ?? [];
    this.ancestors = obj?.ancestors ? (obj?.ancestors as MrfiktivFleetViewModelGen[]).map(a => new Fleet(a)) : [];

    this.companyId = obj?.companyId;
    if (this.companyId) {
      this._company = new Company({ id: obj?.companyId });
    }

    this.title = obj?.title ?? "";
    this.description = obj?.description ?? "";

    this.assignees = obj?.assignees || [];
    this.refs = Reference.filterDuplicates((obj?.refs ?? []).map(ref => new Reference(ref)));

    this.timestamp = new Timestamp(obj?.timestamp);

    this.configuration = new ProjectConfiguration(obj?.configuration);
    this.utm = obj?.utm ? new UtmParameter(obj?.utm) : null;
  }

  get company() {
    return this._company;
  }

  get vehicles() {
    return this._vehicles;
  }

  get contracts() {
    return this._contracts;
  }

  get companies() {
    return this._companies;
  }

  activeCustomerContract(ref?: string | Date | null) {
    if (!ref) {
      ref = new Date();
    }
    if (!this.contracts) {
      return;
    }

    for (const ancestorId of [...this.ancestorIds, this.id].reverse()) {
      const contract = this.contracts.find(
        c =>
          c.activeContractAtDate(ref) &&
          c.type === ContractTypeEnum.CUSTOMER &&
          c.refs.find(ref => ref.refId === ancestorId)
      );
      if (contract) {
        return contract;
      }
    }
  }

  /**
   * fetch object
   */
  async fetch(): Promise<this> {
    const res = await fleetService.findOne(this.partnerId, this.id);

    this.map(res);
    FleetDataAccessLayer.set(this);

    return this;
  }

  /**
   * map props from viewmodel to this
   */
  protected map(obj?: MrfiktivFleetViewModelGen) {
    if (!obj) return;
    this.id = obj?.id ?? "";
    this.partnerId = obj?.partnerId ?? "";
    this.userId = obj?.userId ?? "";

    this.isRoot = obj?.isRoot ?? false;
    this.rootId = obj?.rootId;
    this.parentId = obj?.parentId;
    this.ancestorIds = obj?.ancestorIds ?? [];
    this.ancestors = obj?.ancestors ? (obj?.ancestors as MrfiktivFleetViewModelGen[]).map(a => new Fleet(a)) : [];

    this.companyId = obj?.companyId;

    this.title = obj?.title ?? "";
    this.description = obj?.description ?? "";

    this.assignees = obj?.assignees || [];
    this.refs = obj?.refs || [];

    this.timestamp = new Timestamp(obj?.timestamp);

    this.configuration = new ProjectConfiguration(obj?.configuration);
    this.utm = obj?.utm ? new UtmParameter(obj?.utm) : null;
  }

  /**
   * create object
   */
  async create() {
    const data: MrfiktivCreateFleetDtoGen = {
      title: this.title,
      description: this.description,
      assignees: this.assignees,
      parentId: this.parentId,
      companyId: this.companyId,
      utm: this.utm,
      refs: Reference.filterDuplicates((this.refs ?? []) as IReference[])
    };
    const res = await fleetService.create(this.partnerId, data);

    this.map(res);

    FleetDataAccessLayer.set(this);

    return this;
  }

  /**
   * delete object
   */
  async delete() {
    const res = await fleetService.remove(this.partnerId, this.id);

    this.map(res);
    FleetDataAccessLayer.delete(this);
  }

  /**
   * update object
   * @returns
   */
  async update() {
    const data: MrfiktivUpdateFleetDtoGen = {
      title: this.title,
      description: this.description,
      assignees: this.assignees,
      parentId: this.parentId,
      companyId: this.companyId,
      refs: Reference.filterDuplicates((this.refs ?? []) as IReference[])
    };
    const res = await fleetService.update(this.partnerId, this.id, data);
    this.map(res);
    FleetDataAccessLayer.set(this);

    return this;
  }

  /**
   * update object via dto
   * @param dto
   * @returns
   */
  async updatePartial(dto: MrfiktivUpdateFleetDtoGen) {
    const res = await fleetService.update(this.partnerId, this.id, dto);

    this.map(res);

    FleetDataAccessLayer.set(this);

    return this;
  }

  async fetchCompany() {
    if (!this.companyId || !this.partnerId) {
      return;
    }

    this._isLoadingCompany = true;

    return await companyService
      .getOne(this.partnerId, this.companyId)
      .then(r => (this._company = new Company(r)))
      .finally(() => (this._isLoadingCompany = false));
  }

  async fetchNextRootCompany() {
    if (!this.partnerId) {
      return;
    }

    const fleets = await fleetService.findAllAncestorsForFleet(this.partnerId, this.id);
    for (const fleet of fleets.reverse()) {
      if (fleet.companyId) {
        this._company = await new Company({ id: fleet.companyId, partnerId: this.partnerId }).fetch();
        return this._company;
      }
    }
  }

  async fetchVehicles() {
    if (!this.partnerId) {
      return;
    }

    this._isLoadingVehicles = true;

    const res = await vehicleService
      .getAll({
        partnerId: this.partnerId,
        filter: [new PageFilterElement({ key: "groupId", operation: "$eq", value: this.id })]
      })
      .finally(() => (this._isLoadingVehicles = false));

    if (!res.data) {
      return;
    }

    this._vehicles = res.data.map(v => new Vehicle(v));
  }

  async fetchContracts() {
    if (!this.partnerId) {
      return;
    }

    this._isLoadingContracts = true;

    try {
      const res = await contractService.getManyByRefIds(this.partnerId, { ids: [this.id, ...this.ancestorIds] });

      if (!res) {
        return;
      }

      this._contracts = res.map(v => new Contract(v));
    } finally {
      this._isLoadingContracts = false;
    }
  }

  async fetchCompanies() {
    if (!this.partnerId || !this.id) {
      return;
    }

    this._isLoadingCompanies = true;

    try {
      const res = await companyService.get({
        partnerId: this.partnerId,
        filter: [
          new PageFilterElement({
            key: "refs.refId",
            operation: PageFilterOperationEnum.EQUAL,
            value: this.id
          })
        ],
        itemsPerPage: PageDefaults.DEFAULT_PAGE_SIZES[3]
      });

      if (!res) {
        return;
      }

      this._companies = res.data?.map(v => new Company(v));
    } finally {
      this._isLoadingCompanies = false;
    }
  }

  getCompanyByServiceEnum(type: CompanyServiceEnum) {
    if (!this._companies) {
      return;
    }

    return this._companies.find(company => company.services?.includes(type) === true);
  }

  get companyIds(): string[] {
    return [
      ...(this.companyId ? [this.companyId] : []),
      ...this.ancestors.filter(ancestor => !!ancestor.companyId).map(ancestor => ancestor.companyId as string)
    ];
  }

  goTo(router: VueRouter) {
    return {
      detail: () => router.push({ name: "FleetDetailView", params: { fleetId: this.id, partnerId: this.partnerId } })
    };
  }
}

type IFleet = FleetBase;
const Fleet = Filter.createForClass(FleetBase);

export { Fleet, IFleet };
